import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { IconMapPin, IconCreditCard, IconDiscount, IconUsers, IconGift, IconCurrencyRubel } from '@tabler/icons-react';
import { Card } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';

const HomePage = () => {
  const [salons, setSalons] = useState([]);
  const [selectedSalon, setSelectedSalon] = useState(null);
  const [masters, setMasters] = useState([]);
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Auto-scroll function using getElementById - more reliable for conditionally rendered sections
  const scrollToElement = (elementId) => {
    const attemptScroll = (attempts = 0) => {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (attempts < 20) {
        setTimeout(() => attemptScroll(attempts + 1), 100);
      }
    };
    setTimeout(() => attemptScroll(), 300);
  };

  useEffect(() => {
    loadSalons();
  }, []);

  useEffect(() => {
    if (selectedSalon) {
      loadSalonMasters(selectedSalon.id);
      setSelectedMaster(null);
      setCustomAmount('');
    }
  }, [selectedSalon]);

  const loadSalons = async () => {
    try {
      setLoading(true);
      const response = await api.get('/salons');
      setSalons(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки салонов');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadSalonMasters = async (salonId) => {
    try {
      const response = await api.get(`/salons/${salonId}/masters`);
      setMasters(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки мастеров');
      console.error(error);
    }
  };

  const handlePayment = async () => {
    if (!selectedMaster) {
      toast.error('Выберите мастера');
      return;
    }

    const amountRub = parseFloat(customAmount);
    if (!amountRub || amountRub < 1) {
      toast.error('Укажите сумму (минимум 1₽)');
      return;
    }

    try {
      setProcessingPayment(true);

      const response = await api.post('/order', {
        salonId: selectedSalon.id,
        masterId: selectedMaster.id,
        amount: Math.round(amountRub * 100)
      });

      if (response.data.confirmationUrl) {
        window.location.href = response.data.confirmationUrl;
      } else {
        throw new Error('Payment URL not received');
      }
    } catch (error) {
      toast.error('Ошибка создания платежа');
      console.error(error);
    } finally {
      setProcessingPayment(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-noise">
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="py-12 sm:py-16 lg:py-24"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-[-0.01em] mb-4" data-testid="hero-title">
            Стрижки без лишних хлопот
          </h2>
          <p className="text-base sm:text-lg text-[color:hsl(var(--foreground))]/70 max-w-2xl mx-auto mb-8">
            Быстро, стильно, доступно — приходите когда удобно
          </p>
          
          <button
            onClick={() => document.getElementById('salons-section').scrollIntoView({ behavior: 'smooth' })}
            className="mx-auto inline-flex items-center justify-center h-14 px-10 rounded-[128px] bg-[color:hsl(var(--brand-orange))] text-white text-lg font-medium transition-[background-color,transform] duration-200 ease-out hover:bg-[color:hsl(var(--brand-orange-700))] hover:scale-105 shadow-lg"
            data-testid="pay-service-button"
          >
            <IconCreditCard size={24} className="mr-2" />
            Оплатить услугу
          </button>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto mt-12">
            <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[128px] p-6 text-center">
              <IconDiscount size={32} className="text-[color:hsl(var(--brand-orange))] mx-auto mb-3" />
              <h3 className="font-medium mb-2">Каждая 6-я стрижка</h3>
              <p className="text-sm text-[color:hsl(var(--foreground))]/70">Бесплатно!</p>
            </Card>
            <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[128px] p-6 text-center">
              <IconUsers size={32} className="text-[color:hsl(var(--brand-orange))] mx-auto mb-3" />
              <h3 className="font-medium mb-2">Семейный формат</h3>
              <p className="text-sm text-[color:hsl(var(--foreground))]/70">Копите бонусы быстрее</p>
            </Card>
            <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[128px] p-6 text-center">
              <IconGift size={32} className="text-[color:hsl(var(--brand-orange))] mx-auto mb-3" />
              <h3 className="font-medium mb-2">Скидки пенсионерам</h3>
              <p className="text-sm text-[color:hsl(var(--foreground))]/70">Специальные цены</p>
            </Card>
          </div>
        </div>
      </motion.section>

      {/* Step 1: Select Salon (Address) */}
      <section className="pb-12 sm:pb-16" id="salons-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-['Playfair_Display'] text-3xl mb-2 text-center" data-testid="salons-section-title">1. Выберите адрес</h3>
          <p className="text-sm text-[color:hsl(var(--foreground))]/50 text-center mb-8">Выберите удобный салон</p>
          
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-40 rounded-[128px]" />
              ))}
            </div>
          ) : salons.length === 0 ? (
            <div className="text-center py-12 text-[color:hsl(var(--foreground))]/70" data-testid="no-salons">
              Салоны не найдены
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {salons.map(salon => (
                <motion.div
                  key={salon.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                  <Card
                    className={`group relative bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[128px] p-6 sm:p-8 transition-[border-color,box-shadow] duration-200 ease-out hover:border-[color:hsl(var(--brand-orange))] shadow-none cursor-pointer ${
                      selectedSalon?.id === salon.id ? 'border-[color:hsl(var(--brand-orange))]' : ''
                    }`}
                    onClick={() => {
                      setSelectedSalon(salon);
                      // Auto-scroll to masters section after selection
                      scrollToElement('masters-section');
                    }}
                    data-testid="salon-card"
                  >
                    <div className="flex items-center gap-3">
                      <IconMapPin size={18} stroke={1.75} className="text-[color:hsl(var(--brand-orange))]" />
                      <div>
                        <h3 className="font-['Playfair_Display'] text-lg" data-testid="salon-card-title">{salon.name}</h3>
                        <p className="text-sm text-[color:hsl(var(--foreground))]/70" data-testid="salon-card-area">{salon.city}</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Step 2: Select Master */}
      {selectedSalon && (
        <section className="pb-12 sm:pb-16" id="masters-section">
          <div className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="font-['Playfair_Display'] text-3xl mb-2 text-center" data-testid="masters-section-title">2. Выберите мастера</h3>
            <p className="text-sm text-[color:hsl(var(--foreground))]/50 text-center mb-8">{selectedSalon.name}</p>
            
            <div className="space-y-4">
              {masters.map(master => (
                <Card
                  key={master.id}
                  className={`bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[128px] p-5 flex items-center justify-between cursor-pointer transition-[border-color] duration-200 ease-out hover:border-[color:hsl(var(--brand-orange))] ${
                    selectedMaster?.id === master.id ? 'border-[color:hsl(var(--brand-orange))]' : ''
                  }`}
                  onClick={() => {
                    setSelectedMaster(master);
                    // Auto-scroll to amount section after selection
                    scrollToElement('amount-section');
                  }}
                  data-testid="master-item"
                >
                  <div>
                    <p className="text-[14px]" data-testid="master-name">{master.name}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 transition-colors ${
                    selectedMaster?.id === master.id 
                      ? 'border-[color:hsl(var(--brand-orange))] bg-[color:hsl(var(--brand-orange))]' 
                      : 'border-[color:hsl(var(--hairline))]'
                  }`} />
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Step 3: Amount & Payment */}
      {selectedMaster && (
        <section className="pb-12 sm:pb-16" id="amount-section">
          <div className="max-w-[600px] mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="font-['Playfair_Display'] text-3xl mb-2 text-center" data-testid="amount-section-title">3. Сумма и оплата</h3>
            <p className="text-sm text-[color:hsl(var(--foreground))]/50 text-center mb-8">Мастер: {selectedMaster.name}</p>
            
            {/* Amount Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2 text-center">Сумма оплаты</label>
              <div className="relative max-w-xs mx-auto">
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="500"
                  min="1"
                  className="w-full h-14 px-4 pr-12 text-2xl text-center rounded-[24px] bg-card border-[0.5px] border-[color:hsl(var(--hairline))] text-[color:hsl(var(--foreground))] transition-[border-color] duration-200 focus:outline-none focus:border-[color:hsl(var(--brand-orange))] focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]/20 tabular-nums"
                  data-testid="custom-amount-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-lg text-[color:hsl(var(--foreground))]/50">
                  <IconCurrencyRubel size={20} />
                </span>
              </div>
            </div>

            {/* Payment Button */}
            <div className="flex justify-center">
              <button
                onClick={handlePayment}
                disabled={processingPayment || !customAmount}
                className="inline-flex items-center justify-center h-14 px-10 rounded-[128px] bg-[color:hsl(var(--brand-orange))] text-white text-lg font-medium transition-[background-color,transform] duration-200 ease-out hover:bg-[color:hsl(var(--brand-orange-700))] hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                data-testid="pay-button"
              >
                <IconCreditCard size={20} className="mr-2" />
                {processingPayment ? 'Обработка...' : `Оплатить ${customAmount ? customAmount + ' ₽' : ''}`}
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default HomePage;
